import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Constants } from '../../../utils/constants';
import { formatCount } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import PageHeader from '../../shell/PageHeader';
import {
  Button,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  JsonViewer,
  Pagination,
  SearchInput,
  SlideOver,
  TableCard,
} from '../../ui';
import { useServerTable } from '../../../hooks/useServerTable';

// The API sends status as a string ("200"), so a numeric comparison never matches.
const isDelivered = (status) => String(status ?? '').startsWith('2');

const badgeClass = (status) =>
  isDelivered(status) ? 'bg-success-500/10 text-success-700' : 'bg-danger-500/10 text-danger-700';

const statusText = (status) => (status === 'transport_error' ? 'No response' : String(status ?? '—'));

const StatusBadge = ({ status }) => <span className={`badge ${badgeClass(status)}`}>{statusText(status)}</span>;

const DetailRow = ({ label, children }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-theme-border py-2 dark:border-themedark-border">
    <span className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">{label}</span>
    <span className="text-base text-theme-headings dark:text-themedark-headings">{children}</span>
  </div>
);

const WebHookLogs = () => {
  const [selected, setSelected] = useState(null);

  // Sorted on created_at rather than the server's default -id, so the ordered column is one the
  // user can actually see and toggle.
  const table = useServerTable(PartnerEndpoints.webhookLogs, {
    sort: '-created_at',
    filters: { webhook_type: '', status: '' },
    fallbackError: 'Could not load webhook logs. Please try again.',
  });

  const columns = [
    {
      key: 'created_at',
      header: 'Date (WAT)',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (log) => <span className="whitespace-nowrap">{formatLagos(log.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Date"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
    {
      key: 'triggered_source',
      header: 'Triggered Source',
      render: (log) =>
        log.triggered_source ? (
          <Link to="/layout/issued-users" onClick={(event) => event.stopPropagation()}>
            {log.triggered_source}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'webhook_type',
      header: 'Webhook Type',
      filter: (
        <FilterSelect
          value={table.filters.webhook_type ?? ''}
          onChange={(value) => table.setFilter('webhook_type', value)}
          options={Constants.webhookTypes}
          label="All types"
          ariaLabel="Filter by webhook type"
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.webhookStatusFilters}
          label="All"
          ariaLabel="Filter by delivery status"
        />
      ),
      render: (log) => <StatusBadge status={log.status} />,
    },
    { key: 'attempt', header: 'Attempt', align: 'center', render: (log) => log.attempt ?? '—' },
    {
      key: 'actions',
      header: 'Details',
      align: 'right',
      render: (log) => (
        <Button
          variant="link"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            setSelected(log);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Webhook Logs" breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Webhook Logs' }]} />

      <p className="mb-4 text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
        Every delivery attempt is listed here. A 5xx, 408, 429 or no response at all is retried, up to six attempts
        over about two and a half hours; any other 4xx is not. Attempts of the same event share one Delivery ID.
      </p>

      <TableCard
        title="Deliveries"
        summary={{ label: 'Showing', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <SearchInput
            value={table.search}
            onChange={table.setSearch}
            placeholder="Search source, type or status"
          />
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(log) => log.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No webhook logs', message: 'No deliveries match the current search or filters.' }}
          onRowClick={setSelected}
        />
      </TableCard>

      <SlideOver isOpen={Boolean(selected)} onClose={() => setSelected(null)} title="Webhook Delivery" width="max-w-2xl">
        {selected && (
          <div className="flex flex-col gap-6">
            <div>
              <DetailRow label="Date (WAT)">{formatLagos(selected.created_at)}</DetailRow>
              <DetailRow label="Triggered Source">{selected.triggered_source ?? '—'}</DetailRow>
              <DetailRow label="Webhook Type">{selected.webhook_type ?? '—'}</DetailRow>
              <DetailRow label="Status">
                <StatusBadge status={selected.status} />
              </DetailRow>
              <DetailRow label="Delivery ID">{selected.delivery_id ?? '—'}</DetailRow>
              <DetailRow label="Attempt">{selected.attempt ?? '—'}</DetailRow>
              <DetailRow label="Duration">
                {selected.duration_ms === null || selected.duration_ms === undefined
                  ? '—'
                  : `${formatCount(selected.duration_ms)} ms`}
              </DetailRow>
            </div>

            <div>
              <h6 className="mb-2 text-f-h6 font-semibold text-theme-headings dark:text-themedark-headings">Payload</h6>
              <JsonViewer data={selected.payload} />
            </div>

            <div>
              <h6 className="mb-2 text-f-h6 font-semibold text-theme-headings dark:text-themedark-headings">Response</h6>
              {/* A null response only means the partner replied with a non-JSON body; status decides success. */}
              {selected.response === null || selected.response === undefined ? (
                <p className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
                  Non-JSON response body
                </p>
              ) : (
                <JsonViewer data={selected.response} />
              )}
            </div>
          </div>
        )}
      </SlideOver>
    </>
  );
};

export default WebHookLogs;
